import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import PropTypes from 'prop-types';
import {
  Building2,
  Settings,
  Plus,
  RefreshCw,
  Wifi,
  WifiOff,
  BarChart3,
  Clock,
  MessageSquare,
  Zap,
  Users,
  Play,
  Pause,
  UserPlus,
  Hammer,
  X,
  Trash2,
  Trophy,
  Sparkles,
  Rocket,
  Armchair,
  Target,
  Box,
  RotateCcw,
  AlertCircle,
} from 'lucide-react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, RoundedBox, Environment } from '@react-three/drei';
import AgentDesk from './AgentDesk';
import ActivityFeed from './ActivityFeed';
import OfficeSettings, { DEFAULT_OFFICE_SETTINGS } from './OfficeSettings';
import OfficeCreationWizard from './OfficeCreationWizard';
import OfficeBuilder from './OfficeBuilder';
import VirtualOffice from './VirtualOffice';
import { OFFICE_BACKGROUNDS, CHARACTER_CATEGORIES, CHANNELS } from './agentcorpThemes';
import VirtualOffice3D from './VirtualOffice3D';
import MilestoneTracker from './MilestoneTracker';
import useWebhookEvents, { EVENT_TYPES } from '../../hooks/useWebhookEvents';
import {
  fetchClaw9Accounts,
  fetchClaw9Conversations,
  fetchMyOfficeConfig,
  saveMyOfficeConfig,
  deleteMyOfficeConfig,
  fetchMyOfficeStats,
  updateMyOfficeStats,
  createMyOfficeAgent,
  updateMyOfficeAgent,
  deleteMyOfficeAgent,
} from '../../api/claw9';

// ============================================
// 3D AVATAR PREVIEW COMPONENTS (for modal)
// ============================================

// Human body preview
const PreviewHumanBody = ({ skinColor = '#f5d0b5', outfitColor, hairColor = '#1f2937', hairStyle = 'short' }) => {
  return (
    <group>
      {/* Torso */}
      <mesh position={[0, 0.42, 0]}>
        <capsuleGeometry args={[0.1, 0.22, 8, 16]} />
        <meshStandardMaterial color={outfitColor} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.75, 0]}>
        <sphereGeometry args={[0.11, 16, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.04, 0.77, 0.09]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.04, 0.77, 0.09]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[-0.04, 0.77, 0.105]}>
        <sphereGeometry args={[0.01, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      <mesh position={[0.04, 0.77, 0.105]}>
        <sphereGeometry args={[0.01, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      {/* Hair */}
      {hairStyle === 'short' && (
        <mesh position={[0, 0.84, -0.02]}>
          <sphereGeometry args={[0.1, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color={hairColor} />
        </mesh>
      )}
      {hairStyle === 'long' && (
        <group>
          <mesh position={[0, 0.84, -0.02]}>
            <sphereGeometry args={[0.1, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={hairColor} />
          </mesh>
          <mesh position={[0, 0.7, -0.08]}>
            <capsuleGeometry args={[0.08, 0.15, 8, 8]} />
            <meshStandardMaterial color={hairColor} />
          </mesh>
        </group>
      )}
      {/* Arms */}
      <mesh position={[-0.15, 0.4, 0]} rotation={[0, 0, 0.3]}>
        <capsuleGeometry args={[0.03, 0.15, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.15, 0.4, 0]} rotation={[0, 0, -0.3]}>
        <capsuleGeometry args={[0.03, 0.15, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.05, 0.12, 0]}>
        <capsuleGeometry args={[0.04, 0.12, 8, 8]} />
        <meshStandardMaterial color="#374151" />
      </mesh>
      <mesh position={[0.05, 0.12, 0]}>
        <capsuleGeometry args={[0.04, 0.12, 8, 8]} />
        <meshStandardMaterial color="#374151" />
      </mesh>
    </group>
  );
};

// Dog body preview
const PreviewDogBody = ({ furColor = '#8B4513', outfitColor }) => {
  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.35, 0]} rotation={[0.2, 0, 0]}>
        <capsuleGeometry args={[0.12, 0.18, 8, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {outfitColor && (
        <mesh position={[0, 0.38, 0.01]} rotation={[0.2, 0, 0]}>
          <capsuleGeometry args={[0.125, 0.12, 8, 16]} />
          <meshStandardMaterial color={outfitColor} />
        </mesh>
      )}
      {/* Head */}
      <mesh position={[0, 0.65, 0.05]}>
        <sphereGeometry args={[0.13, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.6, 0.16]}>
        <capsuleGeometry args={[0.05, 0.06, 8, 8]} rotation={[Math.PI / 2, 0, 0]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.62, 0.22]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.05, 0.7, 0.12]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.05, 0.7, 0.12]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[-0.05, 0.7, 0.14]}>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      <mesh position={[0.05, 0.7, 0.14]}>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      {/* Ears */}
      <mesh position={[-0.1, 0.78, 0]} rotation={[0, 0, -0.5]}>
        <capsuleGeometry args={[0.03, 0.08, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.1, 0.78, 0]} rotation={[0, 0, 0.5]}>
        <capsuleGeometry args={[0.03, 0.08, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Tail */}
      <mesh position={[0, 0.35, -0.18]} rotation={[-0.8, 0, 0]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.08, 0.15, 0.05]}>
        <capsuleGeometry args={[0.035, 0.12, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.08, 0.15, 0.05]}>
        <capsuleGeometry args={[0.035, 0.12, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[-0.08, 0.15, -0.08]}>
        <capsuleGeometry args={[0.035, 0.12, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.08, 0.15, -0.08]}>
        <capsuleGeometry args={[0.035, 0.12, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
    </group>
  );
};

// Cat body preview
const PreviewCatBody = ({ furColor = '#808080', outfitColor }) => {
  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.32, 0]}>
        <capsuleGeometry args={[0.09, 0.16, 8, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {outfitColor && (
        <mesh position={[0, 0.45, 0]}>
          <torusGeometry args={[0.08, 0.015, 8, 16]} />
          <meshStandardMaterial color={outfitColor} />
        </mesh>
      )}
      {/* Head */}
      <mesh position={[0, 0.58, 0.02]}>
        <sphereGeometry args={[0.1, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Ears */}
      <mesh position={[-0.07, 0.72, 0]} rotation={[0, 0, -0.3]}>
        <coneGeometry args={[0.04, 0.08, 3]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.07, 0.72, 0]} rotation={[0, 0, 0.3]}>
        <coneGeometry args={[0.04, 0.08, 3]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[-0.07, 0.71, 0.01]} rotation={[0, 0, -0.3]}>
        <coneGeometry args={[0.025, 0.05, 3]} />
        <meshStandardMaterial color="#fca5a5" />
      </mesh>
      <mesh position={[0.07, 0.71, 0.01]} rotation={[0, 0, 0.3]}>
        <coneGeometry args={[0.025, 0.05, 3]} />
        <meshStandardMaterial color="#fca5a5" />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.035, 0.6, 0.08]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#22c55e" />
      </mesh>
      <mesh position={[0.035, 0.6, 0.08]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#22c55e" />
      </mesh>
      <mesh position={[-0.035, 0.6, 0.1]}>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      <mesh position={[0.035, 0.6, 0.1]}>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.55, 0.1]}>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#fca5a5" />
      </mesh>
      {/* Tail */}
      <mesh position={[0, 0.35, -0.15]} rotation={[-1, 0, 0]}>
        <capsuleGeometry args={[0.02, 0.15, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.05, 0.12, 0.02]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.05, 0.12, 0.02]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[-0.05, 0.12, -0.05]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.05, 0.12, -0.05]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
    </group>
  );
};

// Robot body preview
const PreviewRobotBody = ({ bodyColor = '#64748b', accentColor = '#3b82f6' }) => {
  return (
    <group>
      {/* Torso */}
      <RoundedBox args={[0.22, 0.28, 0.15]} position={[0, 0.38, 0]} radius={0.02}>
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </RoundedBox>
      {/* Chest light */}
      <mesh position={[0, 0.4, 0.08]}>
        <circleGeometry args={[0.03, 16]} />
        <meshStandardMaterial color={accentColor} emissive={accentColor} emissiveIntensity={1} />
      </mesh>
      {/* Head */}
      <RoundedBox args={[0.18, 0.15, 0.15]} position={[0, 0.62, 0]} radius={0.02}>
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </RoundedBox>
      {/* Visor */}
      <mesh position={[0, 0.63, 0.076]}>
        <boxGeometry args={[0.14, 0.04, 0.01]} />
        <meshStandardMaterial color={accentColor} emissive={accentColor} emissiveIntensity={0.5} />
      </mesh>
      {/* Antenna */}
      <mesh position={[0, 0.75, 0]}>
        <cylinderGeometry args={[0.01, 0.01, 0.08]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0, 0.8, 0]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color={accentColor} emissive={accentColor} emissiveIntensity={1} />
      </mesh>
      {/* Arms */}
      <mesh position={[-0.16, 0.4, 0]}>
        <capsuleGeometry args={[0.03, 0.15, 8, 8]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0.16, 0.4, 0]}>
        <capsuleGeometry args={[0.03, 0.15, 8, 8]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Hands */}
      <mesh position={[-0.16, 0.25, 0]}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color={accentColor} metalness={0.5} roughness={0.3} />
      </mesh>
      <mesh position={[0.16, 0.25, 0]}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color={accentColor} metalness={0.5} roughness={0.3} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.06, 0.12, 0]}>
        <capsuleGeometry args={[0.035, 0.1, 8, 8]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0.06, 0.12, 0]}>
        <capsuleGeometry args={[0.035, 0.1, 8, 8]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Feet */}
      <RoundedBox args={[0.06, 0.03, 0.08]} position={[-0.06, 0.02, 0.01]} radius={0.01}>
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </RoundedBox>
      <RoundedBox args={[0.06, 0.03, 0.08]} position={[0.06, 0.02, 0.01]} radius={0.01}>
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </RoundedBox>
    </group>
  );
};

// Glasses accessory preview
const PreviewGlasses = ({ type = 'round', color = '#1f2937', bodyType = 'human' }) => {
  const yOffset = bodyType === 'dog' ? 0.7 : bodyType === 'cat' ? 0.6 : 0.77;
  const zOffset = bodyType === 'dog' ? 0.14 : bodyType === 'cat' ? 0.1 : 0.11;
  if (type === 'none') return null;

  return (
    <group position={[0, yOffset, zOffset]}>
      {type === 'round' && (
        <>
          <mesh position={[-0.04, 0, 0]}>
            <torusGeometry args={[0.025, 0.004, 8, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0.04, 0, 0]}>
            <torusGeometry args={[0.025, 0.004, 8, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, -0.005]}>
            <boxGeometry args={[0.03, 0.004, 0.004]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'square' && (
        <>
          <mesh position={[-0.04, 0, 0]}>
            <boxGeometry args={[0.05, 0.035, 0.005]} />
            <meshStandardMaterial color={color} transparent opacity={0.3} />
          </mesh>
          <mesh position={[0.04, 0, 0]}>
            <boxGeometry args={[0.05, 0.035, 0.005]} />
            <meshStandardMaterial color={color} transparent opacity={0.3} />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.12, 0.004, 0.004]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'sunglasses' && (
        <>
          <mesh position={[-0.04, 0, 0]}>
            <boxGeometry args={[0.055, 0.03, 0.008]} />
            <meshStandardMaterial color="#1f2937" />
          </mesh>
          <mesh position={[0.04, 0, 0]}>
            <boxGeometry args={[0.055, 0.03, 0.008]} />
            <meshStandardMaterial color="#1f2937" />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.025, 0.006, 0.006]} />
            <meshStandardMaterial color="#1f2937" />
          </mesh>
        </>
      )}
    </group>
  );
};

// Hat accessory preview
const PreviewHat = ({ type = 'none', color = '#1f2937', bodyType = 'human' }) => {
  const yOffset = bodyType === 'dog' ? 0.82 : bodyType === 'cat' ? 0.72 : 0.88;
  if (type === 'none') return null;

  return (
    <group position={[0, yOffset, 0]}>
      {type === 'tophat' && (
        <>
          <mesh position={[0, 0.06, 0]}>
            <cylinderGeometry args={[0.06, 0.06, 0.12, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[0.1, 0.1, 0.015, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0.02, 0]}>
            <cylinderGeometry args={[0.062, 0.062, 0.02, 16]} />
            <meshStandardMaterial color="#dc2626" />
          </mesh>
        </>
      )}
      {type === 'cap' && (
        <>
          <mesh position={[0, 0.02, 0]}>
            <sphereGeometry args={[0.1, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0.08]} rotation={[-0.3, 0, 0]}>
            <boxGeometry args={[0.12, 0.01, 0.08]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'beanie' && (
        <>
          <mesh position={[0, 0.03, 0]}>
            <sphereGeometry args={[0.11, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <torusGeometry args={[0.1, 0.02, 8, 16]} rotation={[Math.PI / 2, 0, 0]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0.12, 0]}>
            <sphereGeometry args={[0.03, 8, 8]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
        </>
      )}
      {type === 'crown' && (
        <>
          <mesh position={[0, 0.03, 0]}>
            <cylinderGeometry args={[0.09, 0.1, 0.06, 6]} />
            <meshStandardMaterial color="#fbbf24" metalness={0.8} roughness={0.2} />
          </mesh>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <mesh key={i} position={[Math.cos((i * Math.PI) / 3) * 0.08, 0.08, Math.sin((i * Math.PI) / 3) * 0.08]}>
              <coneGeometry args={[0.02, 0.04, 4]} />
              <meshStandardMaterial color="#fbbf24" metalness={0.8} roughness={0.2} />
            </mesh>
          ))}
          <mesh position={[0, 0.03, 0.095]}>
            <sphereGeometry args={[0.015, 8, 8]} />
            <meshStandardMaterial color="#dc2626" emissive="#dc2626" emissiveIntensity={0.3} />
          </mesh>
        </>
      )}
      {type === 'cowboy' && (
        <>
          <mesh position={[0, 0.04, 0]}>
            <sphereGeometry args={[0.09, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0]} rotation={[0.1, 0, 0]}>
            <cylinderGeometry args={[0.15, 0.15, 0.015, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
    </group>
  );
};

// Tie accessory preview
const PreviewTie = ({ type = 'none', color = '#dc2626' }) => {
  if (type === 'none') return null;

  return (
    <group position={[0, 0.48, 0.1]}>
      {type === 'necktie' && (
        <>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.03, 0.02, 0.015]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, -0.08, 0]} rotation={[0.1, 0, 0]}>
            <boxGeometry args={[0.04, 0.12, 0.01]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, -0.16, 0.005]} rotation={[0.1, 0, Math.PI / 4]}>
            <boxGeometry args={[0.03, 0.03, 0.01]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'bowtie' && (
        <>
          <mesh position={[-0.025, 0, 0]} rotation={[0, 0, 0.3]}>
            <boxGeometry args={[0.04, 0.025, 0.015]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0.025, 0, 0]} rotation={[0, 0, -0.3]}>
            <boxGeometry args={[0.04, 0.025, 0.015]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0.005]}>
            <boxGeometry args={[0.015, 0.02, 0.01]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
    </group>
  );
};

// Shoes accessory preview
const PreviewShoes = ({ type = 'none', color = '#1f2937', bodyType = 'human' }) => {
  if (type === 'none' || bodyType === 'dog' || bodyType === 'cat') return null;

  return (
    <group position={[0, 0.02, 0]}>
      {type === 'formal' && (
        <>
          <RoundedBox args={[0.055, 0.03, 0.1]} position={[-0.05, 0, 0.02]} radius={0.01}>
            <meshStandardMaterial color={color} />
          </RoundedBox>
          <RoundedBox args={[0.055, 0.03, 0.1]} position={[0.05, 0, 0.02]} radius={0.01}>
            <meshStandardMaterial color={color} />
          </RoundedBox>
        </>
      )}
      {type === 'sneakers' && (
        <>
          <RoundedBox args={[0.06, 0.04, 0.1]} position={[-0.05, 0, 0.02]} radius={0.015}>
            <meshStandardMaterial color="#ffffff" />
          </RoundedBox>
          <RoundedBox args={[0.06, 0.04, 0.1]} position={[0.05, 0, 0.02]} radius={0.015}>
            <meshStandardMaterial color="#ffffff" />
          </RoundedBox>
          <mesh position={[-0.05, 0.01, 0.04]}>
            <boxGeometry args={[0.065, 0.015, 0.04]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0.05, 0.01, 0.04]}>
            <boxGeometry args={[0.065, 0.015, 0.04]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'boots' && (
        <>
          <RoundedBox args={[0.055, 0.08, 0.09]} position={[-0.05, 0.02, 0.01]} radius={0.01}>
            <meshStandardMaterial color={color} />
          </RoundedBox>
          <RoundedBox args={[0.055, 0.08, 0.09]} position={[0.05, 0.02, 0.01]} radius={0.01}>
            <meshStandardMaterial color={color} />
          </RoundedBox>
        </>
      )}
    </group>
  );
};

// Rotating character wrapper
const RotatingCharacter = ({ children, autoRotate }) => {
  const groupRef = useRef();

  useFrame((state) => {
    if (groupRef.current && autoRotate) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.5;
    }
  });

  return <group ref={groupRef}>{children}</group>;
};

// Main Avatar Preview component
const AvatarPreview3D = ({ avatar3D, outfitColor = '#3b82f6' }) => {
  const [autoRotate, setAutoRotate] = useState(true);
  const bodyType = avatar3D?.bodyType || 'human';
  const accessories = avatar3D?.accessories || {};

  const renderBody = () => {
    switch (bodyType) {
      case 'dog':
        return <PreviewDogBody furColor={avatar3D?.furColor || '#8B4513'} outfitColor={outfitColor} />;
      case 'cat':
        return <PreviewCatBody furColor={avatar3D?.furColor || '#808080'} outfitColor={outfitColor} />;
      case 'robot':
        return <PreviewRobotBody bodyColor={avatar3D?.bodyColor || '#64748b'} accentColor={outfitColor} />;
      case 'human':
      default:
        return (
          <PreviewHumanBody
            skinColor={avatar3D?.skinColor || '#f5d0b5'}
            outfitColor={outfitColor}
            hairColor={avatar3D?.hairColor || '#1f2937'}
            hairStyle={avatar3D?.hairStyle || 'short'}
          />
        );
    }
  };

  return (
    <div className="relative w-full h-56 rounded-xl overflow-hidden border border-white/20 bg-gradient-to-b from-slate-800 to-slate-900">
      <Canvas
        camera={{
          position: [0, 0.5, 2.2],
          fov: 35,
        }}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[2, 3, 2]} intensity={0.8} />
        <pointLight position={[-2, 2, 1]} intensity={0.3} color="#fef3c7" />

        <Suspense fallback={null}>
          <group position={[0, -0.35, 0]}>
            <RotatingCharacter autoRotate={autoRotate}>
              {renderBody()}
              <PreviewGlasses type={accessories.glasses} color={accessories.glassesColor || '#1f2937'} bodyType={bodyType} />
              <PreviewHat type={accessories.hat} color={accessories.hatColor || '#1f2937'} bodyType={bodyType} />
              {bodyType === 'human' && <PreviewTie type={accessories.tie} color={accessories.tieColor || '#dc2626'} />}
              <PreviewShoes type={accessories.shoes} color={accessories.shoesColor || '#1f2937'} bodyType={bodyType} />
            </RotatingCharacter>
          </group>
          <Environment preset="city" />
        </Suspense>

        <OrbitControls
          enablePan={false}
          enableZoom={false}
          minPolarAngle={Math.PI / 4}
          maxPolarAngle={Math.PI / 2.2}
          target={[0, 0.15, 0]}
          onStart={() => setAutoRotate(false)}
        />
      </Canvas>

      {/* Rotate hint */}
      <div className="absolute bottom-2 left-2 flex items-center gap-1 text-white/40 text-xs">
        <RotateCcw className="w-3 h-3" />
        <span>Drag to rotate</span>
      </div>

      {/* Auto-rotate toggle */}
      <button
        onClick={() => setAutoRotate(!autoRotate)}
        className={`absolute top-2 right-2 p-1.5 rounded-lg text-xs transition-all ${
          autoRotate ? 'bg-cyan-500/30 text-cyan-300' : 'bg-white/10 text-white/50'
        }`}
        title={autoRotate ? 'Stop auto-rotate' : 'Auto-rotate'}
      >
        <RotateCcw className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// Default milestone stats structure
const DEFAULT_MILESTONE_STATS = {
  today: 0,
  week: 0,
  month: 0,
  allTime: 0,
  todayMessages: 0,
  weekMessages: 0,
  monthMessages: 0,
  allTimeMessages: 0,
  todayGrowth: 0,
  weekGrowth: 0,
  monthGrowth: 0,
};

const normalizeMilestoneStats = (payload) => ({
  ...DEFAULT_MILESTONE_STATS,
  ...(payload?.stats || payload?.data || payload || {}),
});

const hashValue = (value) => String(value || '')
  .split('')
  .reduce((total, char) => total + char.charCodeAt(0), 0);

const resolveAgentForConversation = (conversation, agents) => {
  if (!agents.length) {
    return null;
  }

  const conversationChannel = conversation.channel || 'all';
  const eligibleAgents = agents.filter((agent) => {
    const channels = agent.channels || ['all'];
    return channels.includes('all') || channels.includes(conversationChannel);
  });
  const pool = eligibleAgents.length ? eligibleAgents : agents;
  const seed = conversation.thread_id || conversation.id || conversation.contact_id || conversation.contact_name;
  return pool[hashValue(seed) % pool.length] || null;
};

/**
 * Agent Edit Modal - for adding/editing agents
 */
// 3D Avatar configuration options
const BODY_TYPES = [
  { id: 'human', name: 'Human', icon: '🧑' },
  { id: 'dog', name: 'Dog', icon: '🐕' },
  { id: 'cat', name: 'Cat', icon: '🐱' },
  { id: 'robot', name: 'Robot', icon: '🤖' },
];

const ACCESSORY_OPTIONS = {
  glasses: [
    { id: 'none', name: 'None' },
    { id: 'round', name: 'Round' },
    { id: 'square', name: 'Square' },
    { id: 'sunglasses', name: 'Sunglasses' },
  ],
  hat: [
    { id: 'none', name: 'None' },
    { id: 'tophat', name: 'Top Hat' },
    { id: 'cap', name: 'Cap' },
    { id: 'beanie', name: 'Beanie' },
    { id: 'crown', name: 'Crown' },
    { id: 'cowboy', name: 'Cowboy' },
  ],
  tie: [
    { id: 'none', name: 'None' },
    { id: 'necktie', name: 'Necktie' },
    { id: 'bowtie', name: 'Bow Tie' },
  ],
  shoes: [
    { id: 'none', name: 'None' },
    { id: 'formal', name: 'Formal' },
    { id: 'sneakers', name: 'Sneakers' },
    { id: 'boots', name: 'Boots' },
  ],
};

const COLOR_PRESETS = {
  fur: ['#8B4513', '#D2691E', '#F4A460', '#808080', '#2F4F4F', '#FFFFFF', '#1a1a1a'],
  skin: ['#f5d0b5', '#e8beac', '#d4a574', '#c68642', '#8d5524', '#6b4423'],
  hair: ['#1f2937', '#92400e', '#fbbf24', '#dc2626', '#7c3aed', '#ffffff'],
  accessory: ['#1f2937', '#dc2626', '#2563eb', '#16a34a', '#fbbf24', '#ec4899', '#8b5cf6'],
};

// Map emoji categories to default 3D body types
const getCategoryBodyType = (categoryId, emoji) => {
  // Check specific emojis first
  const dogEmojis = ['🐕', '🐶', '🐩', '🐕‍🦺', '🦮'];
  const catEmojis = ['🐱', '🐈', '🐈‍⬛', '😺', '😸'];
  const robotEmojis = ['🤖', '🦾', '🦿'];

  if (dogEmojis.includes(emoji)) return 'dog';
  if (catEmojis.includes(emoji)) return 'cat';
  if (robotEmojis.includes(emoji)) return 'robot';
  if (categoryId === 'robots') return 'robot';
  if (categoryId === 'animals') {
    if (emoji.includes('🐕') || emoji.includes('🐶')) return 'dog';
    if (emoji.includes('🐱') || emoji.includes('🐈')) return 'cat';
    return 'dog'; // Default animals to dog
  }
  return 'human';
};

const AgentEditModal = ({ agent, onSave, onClose, onDelete, category }) => {
  // Find which category the agent's emoji belongs to
  const findCategoryForEmoji = (emoji) => {
    for (const [catId, cat] of Object.entries(CHARACTER_CATEGORIES)) {
      if (cat.characters.some(c => c.emoji === emoji)) {
        return catId;
      }
    }
    return 'professionals'; // Default to professionals (human characters)
  };

  const [name, setName] = useState(agent?.name || '');
  const [selectedEmoji, setSelectedEmoji] = useState(agent?.emoji || '👩‍💼');
  const [selectedChannels, setSelectedChannels] = useState(agent?.channels || ['all']);
  const [selectedCategory, setSelectedCategory] = useState(
    agent?.emoji ? findCategoryForEmoji(agent.emoji) : 'professionals'
  );

  // 3D Avatar state
  const [show3DOptions, setShow3DOptions] = useState(false);
  const [avatar3D, setAvatar3D] = useState(agent?.avatar3D || {
    bodyType: getCategoryBodyType(selectedCategory, selectedEmoji),
    furColor: '#8B4513',
    skinColor: '#f5d0b5',
    hairColor: '#1f2937',
    hairStyle: 'short',
    accessories: {
      glasses: 'none',
      glassesColor: '#1f2937',
      hat: 'none',
      hatColor: '#1f2937',
      tie: 'none',
      tieColor: '#dc2626',
      shoes: 'none',
      shoesColor: '#1f2937',
    }
  });

  // Update body type when emoji changes
  const handleEmojiSelect = (emoji) => {
    setSelectedEmoji(emoji);
    const newBodyType = getCategoryBodyType(selectedCategory, emoji);
    setAvatar3D(prev => ({ ...prev, bodyType: newBodyType }));
  };

  // Get all categories for selection
  const allCategories = Object.values(CHARACTER_CATEGORIES);
  const characters = CHARACTER_CATEGORIES[selectedCategory]?.characters || [];
  const channelOptions = Object.values(CHANNELS);

  const toggleChannel = (channelId) => {
    if (channelId === 'all') {
      setSelectedChannels(['all']);
    } else {
      setSelectedChannels(prev => {
        const withoutAll = prev.filter(c => c !== 'all');
        if (withoutAll.includes(channelId)) {
          const result = withoutAll.filter(c => c !== channelId);
          return result.length === 0 ? ['all'] : result;
        } else {
          return [...withoutAll, channelId];
        }
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-[#0d1424] border border-white/10 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {agent ? 'Edit Agent' : 'Add New Agent'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Name Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            Agent Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Emma, Alex, Sarah..."
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
            autoFocus
          />
        </div>

        {/* Character Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            Choose Character
          </label>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-1 mb-3">
            {allCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all
                  ${selectedCategory === cat.id
                    ? 'bg-[#ff4d4d] text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
              >
                <span>{cat.icon}</span>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>

          {/* Character Grid */}
          <div className="grid grid-cols-6 gap-2 max-h-32 overflow-y-auto p-1 bg-white/5 rounded-lg">
            {characters.map((char) => (
              <button
                key={char.id}
                onClick={() => handleEmojiSelect(char.emoji)}
                className={`p-2 rounded-lg border-2 transition-all text-2xl
                  ${selectedEmoji === char.emoji
                    ? 'border-[#ff4d4d] bg-[#ff4d4d]/10'
                    : 'border-white/10 hover:border-white/20'
                  }`}
                title={char.name}
              >
                {char.emoji}
              </button>
            ))}
          </div>
        </div>

        {/* 3D Avatar Preview */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            3D Preview
          </label>
          <AvatarPreview3D
            avatar3D={avatar3D}
            outfitColor={characters.find(c => c.emoji === selectedEmoji)?.color || '#3b82f6'}
          />
        </div>

        {/* 3D Avatar Customization */}
        <div className="mb-4">
          <button
            onClick={() => setShow3DOptions(!show3DOptions)}
            className="flex items-center justify-between w-full text-sm font-medium text-white/70 mb-2 hover:text-white transition-colors"
          >
            <span className="flex items-center gap-2">
              <Box className="w-4 h-4" />
              Customize 3D Avatar
            </span>
            <span className={`transform transition-transform ${show3DOptions ? 'rotate-180' : ''}`}>▼</span>
          </button>

          {show3DOptions && (
            <div className="p-3 bg-white/5 rounded-xl space-y-3 border border-white/10">
              {/* Body Type */}
              <div>
                <label className="block text-xs text-white/50 mb-1.5">Body Type</label>
                <div className="flex gap-1.5">
                  {BODY_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setAvatar3D(prev => ({ ...prev, bodyType: type.id }))}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all
                        ${avatar3D.bodyType === type.id
                          ? 'bg-[#ff4d4d] text-white'
                          : 'bg-white/10 text-white/60 hover:bg-white/20'
                        }`}
                    >
                      <span>{type.icon}</span>
                      <span>{type.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Options based on body type */}
              {avatar3D.bodyType === 'human' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/50 mb-1.5">Skin Color</label>
                    <div className="flex gap-1 flex-wrap">
                      {COLOR_PRESETS.skin.map((color) => (
                        <button
                          key={color}
                          onClick={() => setAvatar3D(prev => ({ ...prev, skinColor: color }))}
                          className={`w-6 h-6 rounded-full border-2 transition-all
                            ${avatar3D.skinColor === color ? 'border-[#ff4d4d] scale-110' : 'border-white/20'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1.5">Hair Color</label>
                    <div className="flex gap-1 flex-wrap">
                      {COLOR_PRESETS.hair.map((color) => (
                        <button
                          key={color}
                          onClick={() => setAvatar3D(prev => ({ ...prev, hairColor: color }))}
                          className={`w-6 h-6 rounded-full border-2 transition-all
                            ${avatar3D.hairColor === color ? 'border-[#ff4d4d] scale-110' : 'border-white/20'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {(avatar3D.bodyType === 'dog' || avatar3D.bodyType === 'cat') && (
                <div>
                  <label className="block text-xs text-white/50 mb-1.5">Fur Color</label>
                  <div className="flex gap-1 flex-wrap">
                    {COLOR_PRESETS.fur.map((color) => (
                      <button
                        key={color}
                        onClick={() => setAvatar3D(prev => ({ ...prev, furColor: color }))}
                        className={`w-6 h-6 rounded-full border-2 transition-all
                          ${avatar3D.furColor === color ? 'border-[#ff4d4d] scale-110' : 'border-white/20'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Accessories */}
              <div className="border-t border-white/10 pt-3">
                <label className="block text-xs text-white/50 mb-2">Accessories</label>

                {/* Glasses */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-white/40 w-16">Glasses:</span>
                  <div className="flex gap-1 flex-wrap">
                    {ACCESSORY_OPTIONS.glasses.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setAvatar3D(prev => ({
                          ...prev,
                          accessories: { ...prev.accessories, glasses: opt.id }
                        }))}
                        className={`px-2 py-0.5 rounded text-xs transition-all
                          ${avatar3D.accessories.glasses === opt.id
                            ? 'bg-cyan-500/30 text-cyan-300'
                            : 'bg-white/10 text-white/50 hover:bg-white/20'
                          }`}
                      >
                        {opt.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Hat */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-white/40 w-16">Hat:</span>
                  <div className="flex gap-1 flex-wrap">
                    {ACCESSORY_OPTIONS.hat.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setAvatar3D(prev => ({
                          ...prev,
                          accessories: { ...prev.accessories, hat: opt.id }
                        }))}
                        className={`px-2 py-0.5 rounded text-xs transition-all
                          ${avatar3D.accessories.hat === opt.id
                            ? 'bg-cyan-500/30 text-cyan-300'
                            : 'bg-white/10 text-white/50 hover:bg-white/20'
                          }`}
                      >
                        {opt.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tie (only for humans) */}
                {avatar3D.bodyType === 'human' && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-white/40 w-16">Tie:</span>
                    <div className="flex gap-1 flex-wrap">
                      {ACCESSORY_OPTIONS.tie.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => setAvatar3D(prev => ({
                            ...prev,
                            accessories: { ...prev.accessories, tie: opt.id }
                          }))}
                          className={`px-2 py-0.5 rounded text-xs transition-all
                            ${avatar3D.accessories.tie === opt.id
                              ? 'bg-cyan-500/30 text-cyan-300'
                              : 'bg-white/10 text-white/50 hover:bg-white/20'
                            }`}
                        >
                          {opt.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Shoes (only for humans/robots) */}
                {(avatar3D.bodyType === 'human' || avatar3D.bodyType === 'robot') && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-white/40 w-16">Shoes:</span>
                    <div className="flex gap-1 flex-wrap">
                      {ACCESSORY_OPTIONS.shoes.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => setAvatar3D(prev => ({
                            ...prev,
                            accessories: { ...prev.accessories, shoes: opt.id }
                          }))}
                          className={`px-2 py-0.5 rounded text-xs transition-all
                            ${avatar3D.accessories.shoes === opt.id
                              ? 'bg-cyan-500/30 text-cyan-300'
                              : 'bg-white/10 text-white/50 hover:bg-white/20'
                            }`}
                        >
                          {opt.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Accessory Colors */}
                {(avatar3D.accessories.glasses !== 'none' || avatar3D.accessories.hat !== 'none' || avatar3D.accessories.tie !== 'none') && (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <span className="text-xs text-white/40">Accessory Color:</span>
                    <div className="flex gap-1 mt-1">
                      {COLOR_PRESETS.accessory.map((color) => (
                        <button
                          key={color}
                          onClick={() => setAvatar3D(prev => ({
                            ...prev,
                            accessories: {
                              ...prev.accessories,
                              glassesColor: color,
                              hatColor: color,
                              tieColor: color,
                              shoesColor: color,
                            }
                          }))}
                          className={`w-5 h-5 rounded-full border-2 transition-all
                            ${avatar3D.accessories.hatColor === color ? 'border-[#ff4d4d] scale-110' : 'border-white/20'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Channel Assignment */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            Assigned Channels
          </label>
          <div className="flex flex-wrap gap-2">
            {channelOptions.map((channel) => (
              <button
                key={channel.id}
                onClick={() => toggleChannel(channel.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                  ${selectedChannels.includes(channel.id)
                    ? 'text-white'
                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                  }`}
                style={selectedChannels.includes(channel.id) ? { backgroundColor: channel.color + '30', borderColor: channel.color, border: '1px solid' } : {}}
              >
                <span>{channel.icon}</span>
                <span>{channel.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {agent && onDelete && (
            <button
              onClick={() => {
                if (window.confirm(`Remove ${agent.name} from your office?`)) {
                  onDelete(agent.id);
                  onClose();
                }
              }}
              className="px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-white/60 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (name.trim()) {
                const char = characters.find(c => c.emoji === selectedEmoji);
                onSave({
                  ...agent,
                  id: agent?.id || `agent_${Date.now()}`,
                  name: name.trim(),
                  emoji: selectedEmoji,
                  color: char?.color || '#3b82f6',
                  channels: selectedChannels,
                  activeChats: agent?.activeChats || 0,
                  responsesCount: agent?.responsesCount || 0,
                  avatar3D: avatar3D,
                });
                onClose();
              }
            }}
            disabled={!name.trim()}
            className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all
              ${name.trim()
                ? 'bg-[#ff4d4d] text-white hover:bg-[#ff6666]'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
          >
            {agent ? 'Save' : 'Add Agent'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Stats Panel Component
 */
const StatsPanel = ({ agents, events, settings, activeEvents, milestoneStats, unreadTotal = 0 }) => {
  const responseEvents = events.filter((event) => event.type === EVENT_TYPES.AGENT_RESPONDED);
  const totalIncomingMessages = Math.max(milestoneStats?.today || 0, unreadTotal || 0);
  const totalResponses = milestoneStats?.todayMessages || 0;

  // Active agents = agents currently handling a message (in activeEvents)
  const activeAgentIds = Object.keys(activeEvents || {});
  const activeAgents = activeAgentIds.length;

  const avgResponseTime = responseEvents.length > 0
    ? Math.round(responseEvents.filter(e => e.data?.responseTime).reduce((sum, e) => sum + e.data.responseTime, 0) / responseEvents.length)
    : 0;

  if (!settings?.showStats) return null;

  return (
    <div className="bg-[#0d1424] rounded-2xl border border-white/10 p-4">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-[#ff4d4d]" />
        <h3 className="text-sm font-semibold text-white">Statistics</h3>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/5 rounded-xl p-3">
          <div className="flex items-center gap-2 text-white/50 mb-1">
            <Users className="w-3.5 h-3.5" />
            <span className="text-xs">Active</span>
          </div>
          <p className="text-xl font-bold text-white">{activeAgents}/{agents.length}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="flex items-center gap-2 text-white/50 mb-1">
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="text-xs">Incoming</span>
          </div>
          <p className="text-xl font-bold text-white">{totalIncomingMessages}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="flex items-center gap-2 text-white/50 mb-1">
            <Zap className="w-3.5 h-3.5" />
            <span className="text-xs">Responses</span>
          </div>
          <p className="text-xl font-bold text-white">{totalResponses}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="flex items-center gap-2 text-white/50 mb-1">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-xs">Avg Time</span>
          </div>
          <p className="text-xl font-bold text-white">{avgResponseTime}s</p>
        </div>
      </div>
    </div>
  );
};

/**
 * Empty State - No office configured
 */
const EmptyState = ({ onCreateOffice }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-8">
    <div className="w-24 h-24 bg-gradient-to-br from-[#ff4d4d]/20 to-[#ff6b6b]/20 rounded-3xl flex items-center justify-center mb-6 animate-float">
      <Building2 className="w-12 h-12 text-[#ff4d4d]" />
    </div>
    <h2 className="text-2xl font-bold text-white mb-2">Create Your AI Office</h2>
    <p className="text-white/60 mb-8 max-w-md">
      Set up a virtual workspace to visualize your AI agents serving customers in real-time.
      Choose from robots, people, animals, and more with different art styles!
    </p>
    <button
      onClick={onCreateOffice}
      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] text-white rounded-xl font-semibold hover:from-[#ff6666] hover:to-[#ff8080] transition-all animate-pulse-glow"
    >
      <Plus className="w-5 h-5" />
      Create Office
    </button>
  </div>
);

/**
 * MyOffice - Main office visualization component
 */
const MyOffice = ({ userEmail }) => {
  // Account state - fetched dynamically from API
  const [accountId, setAccountId] = useState(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const [accountError, setAccountError] = useState(null);
  const processedEventIdsRef = useRef(new Set());

  // State
  const [officeConfig, setOfficeConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [isSimulating, setIsSimulating] = useState(true);
  const [agents, setAgents] = useState([]);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showBannerEdit, setShowBannerEdit] = useState(false);
  const [activeView, setActiveView] = useState('office');
  const [isDollhouseView, setIsDollhouseView] = useState(false);
  const [showMilestones, setShowMilestones] = useState(false);
  const [milestoneStats, setMilestoneStats] = useState(DEFAULT_MILESTONE_STATS);
  const [conversationActiveEvents, setConversationActiveEvents] = useState({});
  const [conversationUnreadTotal, setConversationUnreadTotal] = useState(0);
  const conversationSnapshotRef = useRef(new Map());

  // Get settings and theme
  const settings = officeConfig?.settings || DEFAULT_OFFICE_SETTINGS;
  const theme = officeConfig?.theme || { category: 'robots', artStyle: 'modern', environment: 'corporate' };
  const layoutItemCount = officeConfig?.layout?.items?.length || 0;

  // Webhook events hook - pass accountId for real API connection
  const {
    events,
    activeEvents,
    isConnected,
    clearEvents,
    triggerEvent,
    isUsingMockData,
  } = useWebhookEvents({
    accountId,
    agents,
    enabled: isSimulating && agents.length > 0,
    mockInterval: settings.eventInterval || 8000,
    useMockData: !accountId, // Use mock data if no account
  });
  const mergedActiveEvents = React.useMemo(
    () => ({ ...conversationActiveEvents, ...activeEvents }),
    [conversationActiveEvents, activeEvents]
  );

  // Load accounts and config on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingAccount(true);
      setIsLoading(true);

      try {
        // Step 1: Fetch user's accounts
        const accountsResult = await fetchClaw9Accounts();
        const accounts = accountsResult.accounts || [];

        if (accounts.length === 0) {
          console.error('[MyOffice] No accounts found!');
          setAccountError('No accounts found. Please set up your Claw9 account first.');
          setIsLoadingAccount(false);
          setIsLoading(false);
          return;
        }

        // Use the first account
        const userAccountId = accounts[0].account_id;
        setAccountId(userAccountId);
        setAccountError(null);
        setIsLoadingAccount(false);


        // Step 2: Fetch office config for this account
        try {
          const config = await fetchMyOfficeConfig(userAccountId);
          if (config) {
            // Verify the config belongs to this account (if account_id is included in response)
            if (config.account_id && config.account_id !== userAccountId) {
              console.error('[MyOffice] WARNING: Received config for wrong account!', {
                expected: userAccountId,
                received: config.account_id
              });
              // Don't load wrong account's data - treat as if no config found
            } else {
              setOfficeConfig(config);
              setAgents(config.agents || []);
            }
          } else {
            // No config found - create a default office so user sees the office view immediately
            const defaultConfig = {
              account_id: userAccountId,
              name: 'My Office',
              theme: { category: 'professionals', artStyle: 'modern', environment: 'corporate' },
              settings: { layout: 'grid', agentSize: 'medium', showStats: true, showActivityFeed: true },
              agents: [],
              layout: { floorTheme: 'wood', items: [] }
            };
            setOfficeConfig(defaultConfig);
            setAgents([]);
            // Save to backend so it persists
            try {
              await saveMyOfficeConfig(userAccountId, defaultConfig);
              console.log('[MyOffice] Created default office config');
            } catch (saveErr) {
              console.warn('[MyOffice] Failed to save default config:', saveErr);
            }
          }
        } catch (configErr) {
          console.warn('[MyOffice] Failed to load office config:', configErr);
          // Config not found is okay - user will create one
        }

        // Step 3: Fetch milestone stats for this account
        try {
          const stats = await fetchMyOfficeStats(userAccountId);
          if (stats) {
            setMilestoneStats(normalizeMilestoneStats(stats));
          }
        } catch (statsErr) {
          console.warn('[MyOffice] Failed to load milestone stats:', statsErr);
          // Stats not found is okay - use defaults
        }

      } catch (err) {
        console.error('[MyOffice] Failed to load accounts:', err);
        setAccountError(err.message || 'Failed to load account information');
      } finally {
        setIsLoading(false);
        setIsLoadingAccount(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    processedEventIdsRef.current = new Set();
  }, [accountId, isUsingMockData]);

  useEffect(() => {
    conversationSnapshotRef.current = new Map();
    setConversationActiveEvents({});
    setConversationUnreadTotal(0);
  }, [accountId]);

  useEffect(() => {
    if (!accountId || !isSimulating) {
      setConversationActiveEvents({});
      return undefined;
    }

    let cancelled = false;
    let pollingInterval = null;

    const syncConversations = async (isInitial = false) => {
      try {
        const result = await fetchClaw9Conversations({ accountId, limit: 100 });
        if (cancelled) {
          return;
        }

        const conversations = result.conversations || result.data?.conversations || result.data || [];
        if (!Array.isArray(conversations)) {
          console.debug('[MyOffice Debug] Conversations payload was not an array', {
            accountId,
            payload: result,
          });
          return;
        }

        const now = Date.now();
        const nextSnapshot = new Map();
        const nextActiveEvents = {};
        let incomingDelta = 0;
        let unreadTotal = 0;

        conversations.forEach((conversation) => {
          const threadId = conversation.thread_id || conversation.id;
          if (!threadId) {
            return;
          }

          const unreadCount = Number(conversation.unread_count || 0);
          unreadTotal += unreadCount;
          const lastMessageAt = conversation.last_message_at || conversation.updated_at || conversation.created_at || null;
          const previous = conversationSnapshotRef.current.get(threadId);
          nextSnapshot.set(threadId, {
            unreadCount,
            lastMessageAt,
          });

          if (!isInitial && previous) {
            const unreadDelta = Math.max(unreadCount - (previous.unreadCount || 0), 0);
            const hasNewTimestamp = lastMessageAt && previous.lastMessageAt && new Date(lastMessageAt).getTime() > new Date(previous.lastMessageAt).getTime();
            if (unreadDelta > 0) {
              incomingDelta += unreadDelta;
            } else if (hasNewTimestamp && unreadCount > 0) {
              incomingDelta += 1;
            }
          }

          const lastActivityTime = lastMessageAt ? new Date(lastMessageAt).getTime() : 0;
          const isRecentlyActive = lastActivityTime && now - lastActivityTime < 2 * 60 * 1000;
          if (unreadCount > 0 || isRecentlyActive) {
            const assignedAgent = resolveAgentForConversation(conversation, agents);
            if (assignedAgent) {
              nextActiveEvents[assignedAgent.id] = {
                id: `conversation:${threadId}`,
                type: EVENT_TYPES.NEW_MESSAGE,
                timestamp: lastMessageAt || new Date().toISOString(),
                customer: {
                  id: conversation.contact_id || threadId,
                  name: conversation.contact_name || conversation.customer_name || 'Unknown',
                  channel: conversation.channel || 'zalo',
                  avatar: conversation.contact_avatar || null,
                },
                agent: {
                  id: assignedAgent.id,
                  name: assignedAgent.name,
                },
                data: {
                  conversationId: threadId,
                  message: conversation.last_message || '',
                  unreadCount,
                },
              };
            }
          }
        });

        conversationSnapshotRef.current = nextSnapshot;
        setConversationActiveEvents(nextActiveEvents);
        setConversationUnreadTotal(unreadTotal);

        console.debug('[MyOffice Debug] Conversation sync', {
          accountId,
          conversationCount: conversations.length,
          unreadTotal,
          activeAgentIds: Object.keys(nextActiveEvents),
          sampleConversation: conversations[0]
            ? {
                id: conversations[0].thread_id || conversations[0].id,
                contact: conversations[0].contact_name || conversations[0].customer_name,
                channel: conversations[0].channel,
                unreadCount: conversations[0].unread_count,
                lastMessageAt: conversations[0].last_message_at || conversations[0].updated_at,
              }
            : null,
        });

        if (incomingDelta > 0) {
          setMilestoneStats((prev) => {
            const updated = {
              ...prev,
              today: prev.today + incomingDelta,
              week: prev.week + incomingDelta,
              month: prev.month + incomingDelta,
              allTime: prev.allTime + incomingDelta,
            };

            updateMyOfficeStats(accountId, updated).catch((err) =>
              console.warn('[MyOffice] Failed to sync conversation stats:', err)
            );

            return updated;
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.debug('[MyOffice Debug] Conversation sync failed', {
            accountId,
            error: err?.message || err,
          });
          console.warn('[MyOffice] Failed to poll conversations for office activity:', err);
        }
      }
    };

    syncConversations(true);
    pollingInterval = setInterval(() => {
      syncConversations(false);
    }, 5000);

    return () => {
      cancelled = true;
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [accountId, agents, isSimulating]);

  // Update agent stats and milestone stats based on incoming events
  useEffect(() => {
    if (events.length === 0) return;

    const processedIds = processedEventIdsRef.current;
    if (!isUsingMockData && processedIds.size === 0) {
      events.forEach((event) => {
        if (event?.id) {
          processedIds.add(event.id);
        }
      });
      return;
    }

    const newEvents = [...events]
      .filter((event) => event?.id && !processedIds.has(event.id))
      .reverse();

    if (newEvents.length === 0) {
      return;
    }

    newEvents.forEach((event) => {
      processedIds.add(event.id);
    });

    let incomingCount = 0;
    let responseCount = 0;
    const agentDeltas = {};

    newEvents.forEach((event) => {
      const agentId = event.agent?.id;

      if (event.type === EVENT_TYPES.NEW_MESSAGE || event.type === EVENT_TYPES.CUSTOMER_WAITING) {
        incomingCount += 1;
        if (agentId) {
          agentDeltas[agentId] = {
            activeChats: (agentDeltas[agentId]?.activeChats || 0) + 1,
            responsesCount: agentDeltas[agentId]?.responsesCount || 0,
          };
        }
        return;
      }

      if (event.type === EVENT_TYPES.AGENT_RESPONDED) {
        responseCount += 1;
        if (agentId) {
          agentDeltas[agentId] = {
            activeChats: (agentDeltas[agentId]?.activeChats || 0) - 1,
            responsesCount: (agentDeltas[agentId]?.responsesCount || 0) + 1,
          };
        }
        return;
      }

      if (event.type === EVENT_TYPES.CONVERSATION_CLOSED && agentId) {
        agentDeltas[agentId] = {
          activeChats: (agentDeltas[agentId]?.activeChats || 0) - 1,
          responsesCount: agentDeltas[agentId]?.responsesCount || 0,
        };
      }
    });

    if (Object.keys(agentDeltas).length > 0) {
      setAgents((prev) => prev.map((agent) => {
        const delta = agentDeltas[agent.id];
        if (!delta) {
          return agent;
        }

        return {
          ...agent,
          activeChats: Math.max((agent.activeChats || 0) + (delta.activeChats || 0), 0),
          responsesCount: Math.max((agent.responsesCount || 0) + (delta.responsesCount || 0), 0),
        };
      }));
    }

    if (incomingCount > 0 || responseCount > 0) {
      setMilestoneStats((prev) => {
        const updated = {
          ...prev,
          today: prev.today + incomingCount,
          week: prev.week + incomingCount,
          month: prev.month + incomingCount,
          allTime: prev.allTime + incomingCount,
          todayMessages: (prev.todayMessages || 0) + responseCount,
          weekMessages: (prev.weekMessages || 0) + responseCount,
          monthMessages: (prev.monthMessages || 0) + responseCount,
          allTimeMessages: (prev.allTimeMessages || 0) + responseCount,
        };

        if (accountId) {
          updateMyOfficeStats(accountId, updated).catch((err) =>
            console.warn('[MyOffice] Failed to update stats:', err)
          );
        }

        return updated;
      });
    }
  }, [events, accountId, isUsingMockData]);

  // Handle office creation
  const handleCreateOffice = useCallback(async (config) => {

    // Include account_id in config for ownership verification
    const configWithAccount = {
      ...config,
      account_id: accountId,
    };

    setOfficeConfig(configWithAccount);
    setAgents(configWithAccount.agents || []);

    // Save to API if we have an accountId
    if (accountId) {
      try {
        // Save the office config (name, theme, settings)
        const result = await saveMyOfficeConfig(accountId, configWithAccount);

        // Also create each agent individually since the config endpoint doesn't save agents
        const agentsToCreate = configWithAccount.agents || [];
        if (agentsToCreate.length > 0) {
          console.log(`[MyOffice] Creating ${agentsToCreate.length} agents...`);
          const agentPromises = agentsToCreate.map(agent =>
            createMyOfficeAgent(accountId, agent)
          );
          const agentResults = await Promise.allSettled(agentPromises);

          // Log any failures
          const failures = agentResults.filter(r => r.status === 'rejected');
          if (failures.length > 0) {
            console.error('[MyOffice] Some agents failed to create:', failures);
          }
          console.log(`[MyOffice] Created ${agentResults.filter(r => r.status === 'fulfilled').length}/${agentsToCreate.length} agents`);
        }
      } catch (err) {
        console.error('[MyOffice] Failed to save office config:', err);
        // Show user-visible error
        alert('Failed to save office configuration. Please try again.');
      }
    } else {
      console.warn('[MyOffice] No accountId available - cannot save to API!');
    }
  }, [accountId]);

  // Handle settings save (supports theme switching)
  const handleSaveSettings = useCallback(async (newSettings, newTheme) => {
    const updatedConfig = {
      ...officeConfig,
      settings: newSettings,
      theme: newTheme || officeConfig?.theme,
    };
    setOfficeConfig(updatedConfig);

    // Save to API
    if (accountId) {
      try {
        await saveMyOfficeConfig(accountId, updatedConfig);
      } catch (err) {
        console.error('[MyOffice] Failed to save settings:', err);
      }
    }
  }, [officeConfig, accountId]);

  // Reset office
  const handleResetOffice = useCallback(async () => {
    if (window.confirm('Are you sure you want to reset your office? This will remove all agents and settings.')) {
      setOfficeConfig(null);
      setAgents([]);
      clearEvents();

      // Delete from API
      if (accountId) {
        try {
          await deleteMyOfficeConfig(accountId);
        } catch (err) {
          console.error('[MyOffice] Failed to delete office config:', err);
        }
      }
    }
  }, [clearEvents, accountId]);

  // Add new agent
  const handleAddAgent = useCallback(async (newAgent) => {
    // Save to API first
    if (accountId) {
      try {
        const createdAgent = await createMyOfficeAgent(accountId, newAgent);
        // Use the response from server which may have additional fields
        const agentToAdd = createdAgent || newAgent;
        const updatedAgents = [...agents, agentToAdd];
        setAgents(updatedAgents);
        setOfficeConfig(prev => ({ ...prev, agents: updatedAgents }));
        console.log('[MyOffice] Agent created successfully:', agentToAdd.id);
      } catch (err) {
        console.error('[MyOffice] Failed to create agent:', err);
        // Show error to user - don't update local state if API failed
        alert('Failed to create agent. Please try again.');
      }
    } else {
      // No accountId - just update local state (demo mode)
      const updatedAgents = [...agents, newAgent];
      setAgents(updatedAgents);
      setOfficeConfig(prev => ({ ...prev, agents: updatedAgents }));
    }
  }, [agents, accountId]);

  // Update existing agent
  const handleUpdateAgent = useCallback(async (updatedAgent) => {
    // Save to API first
    if (accountId) {
      try {
        const result = await updateMyOfficeAgent(accountId, updatedAgent.id, updatedAgent);
        const agentData = result || updatedAgent;
        const updatedAgents = agents.map(a => a.id === agentData.id ? agentData : a);
        setAgents(updatedAgents);
        setOfficeConfig(prev => ({ ...prev, agents: updatedAgents }));
        console.log('[MyOffice] Agent updated successfully:', agentData.id);
      } catch (err) {
        console.error('[MyOffice] Failed to update agent:', err);
        alert('Failed to update agent. Please try again.');
      }
    } else {
      // No accountId - just update local state (demo mode)
      const updatedAgents = agents.map(a => a.id === updatedAgent.id ? updatedAgent : a);
      setAgents(updatedAgents);
      setOfficeConfig(prev => ({ ...prev, agents: updatedAgents }));
    }
  }, [agents, accountId]);

  // Delete agent
  const handleDeleteAgent = useCallback(async (agentId) => {
    // Delete from API first
    if (accountId) {
      try {
        await deleteMyOfficeAgent(accountId, agentId);
        const updatedAgents = agents.filter(a => a.id !== agentId);
        setAgents(updatedAgents);
        setOfficeConfig(prev => ({ ...prev, agents: updatedAgents }));
        console.log('[MyOffice] Agent deleted successfully:', agentId);
      } catch (err) {
        console.error('[MyOffice] Failed to delete agent:', err);
        alert('Failed to delete agent. Please try again.');
      }
    } else {
      // No accountId - just update local state (demo mode)
      const updatedAgents = agents.filter(a => a.id !== agentId);
      setAgents(updatedAgents);
      setOfficeConfig(prev => ({ ...prev, agents: updatedAgents }));
    }
  }, [agents, accountId]);

  // Save office layout from builder
  const handleSaveLayout = useCallback(async (layout) => {
    const updatedConfig = { ...officeConfig, layout };
    setOfficeConfig(updatedConfig);
    setShowBuilder(false);

    // Save to API
    if (accountId) {
      try {
        await saveMyOfficeConfig(accountId, updatedConfig);
      } catch (err) {
        console.error('[MyOffice] Failed to save layout:', err);
      }
    }
  }, [officeConfig, accountId]);

  // Get background class
  const getBackgroundClass = () => {
    const bg = OFFICE_BACKGROUNDS[settings.backgroundColor] || OFFICE_BACKGROUNDS.default;
    return bg.className;
  };

  const totalChats = agents.reduce((sum, agent) => sum + (agent.activeChats || 0), 0);
  const totalResponses = agents.reduce((sum, agent) => sum + (agent.responsesCount || 0), 0);
  const officeScore = agents.length * 120 + layoutItemCount * 35 + totalResponses * 12 + totalChats * 8;
  const currentLevel = Math.max(1, Math.floor(officeScore / 300) + 1);
  const nextLevelScore = currentLevel * 300;
  const scoreProgress = Math.min(100, Math.round((officeScore / nextLevelScore) * 100));
  const designMilestones = [
    { id: 'agents', label: 'Crew', value: `${agents.length} agents live`, icon: Users },
    { id: 'props', label: 'Assets', value: `${layoutItemCount} props staged`, icon: Armchair },
    { id: 'response', label: 'Momentum', value: `${totalResponses} responses`, icon: Rocket },
  ];

  // Get layout class
  const getLayoutClass = () => {
    switch (settings.layout) {
      case 'row':
        return 'flex flex-row flex-wrap gap-4 justify-center items-start';
      case 'circle':
        return 'flex flex-wrap gap-4 justify-center items-center';
      default:
        return 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4';
    }
  };

  if (isLoading || isLoadingAccount) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4">
        <RefreshCw className="w-8 h-8 text-[#ff4d4d] animate-spin" />
        <p className="text-white/60 text-sm">
          {isLoadingAccount ? 'Loading account...' : 'Loading office...'}
        </p>
      </div>
    );
  }

  // Show error state if account loading failed
  if (accountError) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-xl font-bold text-white">Unable to Load Account</h2>
        <p className="text-white/60 max-w-md">{accountError}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-[#ff4d4d] text-white rounded-lg hover:bg-[#ff6666] transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Show empty state if no office configured
  if (!officeConfig) {
    return (
      <>
        <EmptyState onCreateOffice={() => setShowWizard(true)} />
        <OfficeCreationWizard
          isOpen={showWizard}
          onClose={() => setShowWizard(false)}
          onCreate={handleCreateOffice}
        />
      </>
    );
  }

  return (
    <div className={`h-full ${getBackgroundClass()} rounded-2xl overflow-hidden flex flex-col`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-black/30 backdrop-blur-sm flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#ff4d4d] to-[#ff6b6b] rounded-xl flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">{officeConfig.name}</h1>
            <div className="flex items-center gap-2 text-xs text-white/50">
              {isConnected ? (
                <>
                  <Wifi className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-400">{isUsingMockData ? 'Demo' : 'Live'}</span>
                </>
              ) : isUsingMockData ? (
                <>
                  <Zap className="w-3 h-3 text-amber-400" />
                  <span className="text-amber-400">Demo Mode</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3 text-red-400" />
                  <span className="text-red-400">Offline</span>
                </>
              )}
              <span className="mx-1">•</span>
              <span>{agents.length} agents</span>
              <span className="mx-1">•</span>
              <span className="capitalize">{theme.artStyle} style</span>
              {accountId && (
                <>
                  <span className="mx-1">•</span>
                  <span className="text-cyan-400/70" title={accountId}>@{accountId.slice(0, 12)}{accountId.length > 12 ? '...' : ''}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Add Agent */}
          <button
            onClick={() => setShowAddAgent(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-[#ff4d4d]/20 text-[#ff4d4d] hover:bg-[#ff4d4d]/30 transition-all"
            title="Add new agent"
          >
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Agent</span>
          </button>

          {/* Dollhouse View Toggle */}
          <button
            onClick={() => setIsDollhouseView(!isDollhouseView)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
              isDollhouseView
                ? 'bg-cyan-500/30 text-cyan-300 border border-cyan-500/50'
                : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'
            }`}
            title="Toggle 3D dollhouse view"
          >
            <Box className="w-4 h-4" />
            <span className="hidden sm:inline">{isDollhouseView ? '3D On' : '3D'}</span>
          </button>

          {/* Office Builder */}
          <button
            onClick={() => setShowBuilder(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-all"
            title="Customize office layout"
          >
            <Hammer className="w-4 h-4" />
            <span className="hidden sm:inline">Builder</span>
          </button>

          {/* Milestones */}
          <button
            onClick={() => setShowMilestones(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-all"
            title="Team milestones & achievements"
          >
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Milestones</span>
          </button>

          {/* Simulation Toggle */}
          <button
            onClick={() => setIsSimulating(!isSimulating)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
              isSimulating
                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                : 'bg-white/5 text-white/50 hover:bg-white/10'
            }`}
            title={isSimulating ? 'Pause simulation' : 'Resume simulation'}
          >
            {isSimulating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span className="hidden sm:inline">{isSimulating ? 'Live' : 'Paused'}</span>
          </button>

          {/* Test Event */}
          <button
            onClick={() => triggerEvent()}
            className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors"
            title="Trigger test event"
          >
            <Zap className="w-4 h-4" />
          </button>

          {/* Settings */}
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors"
            title="Office settings"
          >
            <Settings className="w-4 h-4" />
          </button>

          {/* Reset */}
          <button
            onClick={handleResetOffice}
            className="p-2 bg-white/5 hover:bg-red-500/20 rounded-lg text-white/60 hover:text-red-400 transition-colors"
            title="Reset office"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 min-h-0">
        {/* Agents Area */}
        <div className="flex-1 pt-10 px-4 pb-4 overflow-y-auto">
          <div className="mb-4 rounded-[28px] border border-white/10 bg-black/25 p-4 backdrop-blur-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-[#ffb4b4]">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Gamified Workspace</span>
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {officeConfig?.bannerTitle || "Build a virtual office people want to collect."}
                </h2>
                <p className="mt-2 text-sm text-white/60">
                  {officeConfig?.bannerSubtitle || "Use presets as ready-to-sell scene packs, then customize desks, lounges, and decor until the office feels alive."}
                </p>
                {/* Edit banner button */}
                <button
                  onClick={() => setShowBannerEdit(true)}
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white text-xs transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>Edit Banner</span>
                </button>
              </div>

              <div className="min-w-[260px] rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-white/35">Office Level</p>
                    <p className="mt-1 text-2xl font-bold text-white">Lv. {currentLevel}</p>
                  </div>
                  <div className="rounded-2xl bg-amber-500/15 p-3">
                    <Trophy className="h-5 w-5 text-amber-300" />
                  </div>
                </div>
                <p className="mt-2 text-xs text-white/55">{officeScore} office points toward your next unlockable scene pack.</p>
                <div className="mt-3 h-2 rounded-full bg-white/10">
                  <div className="h-2 rounded-full bg-gradient-to-r from-[#ff4d4d] via-[#ff9366] to-[#fbbf24]" style={{ width: `${scoreProgress}%` }} />
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {designMilestones.map((milestone) => {
                const Icon = milestone.icon;
                return (
                  <div key={milestone.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-white/10 p-2">
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-white">{milestone.label}</p>
                        <p className="text-xs text-white/55">{milestone.value}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            {[
              { id: 'office', label: 'Live Floor' },
              { id: 'board', label: 'Team Board' },
            ].map((view) => (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`rounded-xl px-4 py-2 text-sm font-medium transition-all ${
                  activeView === view.id
                    ? 'bg-[#ff4d4d] text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {view.label}
              </button>
            ))}

            <span className="ml-auto rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-300">
              Preset packs + custom builder ready
            </span>
          </div>

          {activeView === 'office' ? (
            <div className="min-h-[540px]">
              {isDollhouseView ? (
                <VirtualOffice3D
                  agents={agents}
                  activeEvents={mergedActiveEvents}
                  officeName={officeConfig?.name || 'My Office'}
                  bannerTitle={officeConfig?.bannerTitle}
                  layout={officeConfig?.layout}
                  theme={theme}
                  onAgentClick={(agent) => setEditingAgent(agent)}
                />
              ) : (
                <VirtualOffice
                  agents={agents}
                  activeEvents={mergedActiveEvents}
                  officeName={officeConfig?.name || 'My Office'}
                  bannerTitle={officeConfig?.bannerTitle}
                  layout={officeConfig?.layout}
                  theme={theme}
                  settings={settings}
                  isDollhouseView={isDollhouseView}
                  onAgentClick={(agent) => setEditingAgent(agent)}
                />
              )}
            </div>
          ) : (
            <div className={getLayoutClass()}>
              {agents.map((agent) => (
                <AgentDesk
                  key={agent.id}
                  agent={agent}
                  activeEvent={mergedActiveEvents[agent.id]}
                  artStyle={theme.artStyle}
                  size={settings.agentSize}
                  showAccessories={settings.showAccessories !== false}
                  onClick={() => setEditingAgent(agent)}
                />
              ))}
              {/* Add Agent Card */}
              <button
                onClick={() => setShowAddAgent(true)}
                className="w-36 h-48 border-2 border-dashed border-white/20 rounded-2xl flex flex-col items-center justify-center gap-2 text-white/40 hover:border-[#ff4d4d]/50 hover:text-white/60 transition-all hover:bg-white/5"
              >
                <UserPlus className="w-8 h-8" />
                <span className="text-xs">Add Agent</span>
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        {settings.showActivityFeed && (
          <div className="w-72 border-l border-white/10 p-3 flex flex-col gap-3 overflow-y-auto bg-black/20 flex-shrink-0">
            <StatsPanel
              agents={agents}
              events={events}
              settings={settings}
              activeEvents={mergedActiveEvents}
              milestoneStats={milestoneStats}
              unreadTotal={conversationUnreadTotal}
            />
            <div className="flex-1 min-h-0">
              <ActivityFeed events={events} maxHeight="100%" onClear={clearEvents} />
            </div>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      <OfficeSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        theme={theme}
        onSave={handleSaveSettings}
      />

      {/* Creation Wizard (for reset) */}
      <OfficeCreationWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onCreate={handleCreateOffice}
      />

      {/* Add Agent Modal */}
      {showAddAgent && (
        <AgentEditModal
          agent={null}
          category={theme.category}
          onSave={handleAddAgent}
          onClose={() => setShowAddAgent(false)}
        />
      )}

      {/* Edit Agent Modal */}
      {editingAgent && (
        <AgentEditModal
          agent={editingAgent}
          category={theme.category}
          onSave={handleUpdateAgent}
          onDelete={handleDeleteAgent}
          onClose={() => setEditingAgent(null)}
        />
      )}

      {/* Banner Edit Modal */}
      {showBannerEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-lg mx-4 rounded-2xl border border-white/10 bg-[#0d1424] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Edit Company Banner</h3>
              <button
                onClick={() => setShowBannerEdit(false)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const bannerTitle = formData.get('bannerTitle');
                const bannerSubtitle = formData.get('bannerSubtitle');
                try {
                  await saveMyOfficeConfig(accountId, {
                    bannerTitle,
                    bannerSubtitle
                  });
                  setOfficeConfig(prev => ({ ...prev, bannerTitle, bannerSubtitle }));
                  setShowBannerEdit(false);
                } catch (err) {
                  console.error('Failed to save banner:', err);
                  alert('Failed to save banner. Please try again.');
                }
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1.5">
                    Headline
                  </label>
                  <input
                    name="bannerTitle"
                    type="text"
                    defaultValue={officeConfig?.bannerTitle || "Build a virtual office people want to collect."}
                    placeholder="Your company headline"
                    className="w-full px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]/50"
                    maxLength={200}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1.5">
                    Description
                  </label>
                  <textarea
                    name="bannerSubtitle"
                    rows={3}
                    defaultValue={officeConfig?.bannerSubtitle || "Use presets as ready-to-sell scene packs, then customize desks, lounges, and decor until the office feels alive."}
                    placeholder="Your company description"
                    className="w-full px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]/50 resize-none"
                    maxLength={500}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowBannerEdit(false)}
                  className="px-4 py-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] text-white font-medium hover:opacity-90"
                >
                  Save Banner
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Office Builder Modal */}
      {showBuilder && (
        <div className="fixed inset-0 z-50 bg-black/90">
          <div className="h-full flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-[#0d1424]">
              <div className="flex items-center gap-3">
                <Hammer className="w-5 h-5 text-amber-400" />
                <h2 className="text-lg font-bold text-white">Office Builder</h2>
              </div>
              <button
                onClick={() => setShowBuilder(false)}
                className="p-2 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <OfficeBuilder
                initialLayout={officeConfig?.layout}
                onSaveLayout={handleSaveLayout}
                agents={agents}
                activeEvents={mergedActiveEvents}
                officeName={officeConfig?.name || 'My Office'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Milestone Tracker Modal */}
      <MilestoneTracker
        isOpen={showMilestones}
        onClose={() => setShowMilestones(false)}
        stats={milestoneStats}
        onStatsUpdate={async (newStats) => {
          setMilestoneStats(newStats);
          // Save to API
          if (accountId) {
            try {
              await updateMyOfficeStats(accountId, newStats);
            } catch (err) {
              console.warn('[MyOffice] Failed to update milestone stats:', err);
            }
          }
        }}
      />
    </div>
  );
};

MyOffice.propTypes = {
  userEmail: PropTypes.string,
};

export default MyOffice;
